/** Query keys used by `useListQueryState` + `serializeApplicationsFilters` on application lists. */
import { formatDateOnlyForPicker } from './dateOnly';
import {
  EMPTY_APPLICATION_CREATED_DATE_RANGE_FILTER,
  areApplicationCreatedDateRangeFiltersEqual,
  normalizeApplicationCreatedDateRangeFilter,
  resolveApplicationCreatedDateRangeForApi,
} from './applicationCreatedDateRangeFilter';

export const APPLICATION_LIST_URL_PARAM_KEYS = [
  'search',
  'page',
  'pageSize',
  'sort',
  'stage',
  'appType',
  'leaseStart',
  'leaseEnd',
  'createdDatePreset',
  'createdDateStart',
  'createdDateEnd',
  'tenantCount',
];

export const APPLICATION_LIST_URL_PARAM_KEYS_WITH_PREFILTER = [
  ...APPLICATION_LIST_URL_PARAM_KEYS,
  'prefilter',
];

/** Default + URL (de)serialization for application list "extra" filters (stage, type, lease, tenant count). */
export const DEFAULT_APPLICATION_LIST_EXTRA_FILTERS = {
  selectedStage: [],
  selectedAppType: [],
  selectedLeaseDate: {
    startDate: null,
    endDate: null,
  },
  selectedCreatedDate: { ...EMPTY_APPLICATION_CREATED_DATE_RANGE_FILTER },
  selectedTenantCount: [],
};

function parseCreatedDateRangeFromParams(searchParams, fallbackRange) {
  const preset = searchParams.get('createdDatePreset') || fallbackRange?.preset || null;
  const startDate = formatDateOnlyForPicker(
    searchParams.get('createdDateStart') || fallbackRange?.startDate || '',
  );
  const endDate = formatDateOnlyForPicker(
    searchParams.get('createdDateEnd') || fallbackRange?.endDate || '',
  );

  if (!preset && !startDate && !endDate) {
    return normalizeApplicationCreatedDateRangeFilter(fallbackRange);
  }

  if (!preset && (startDate || endDate)) {
    return normalizeApplicationCreatedDateRangeFilter({
      preset: 'custom',
      startDate,
      endDate,
    });
  }

  return normalizeApplicationCreatedDateRangeFilter({
    preset,
    startDate,
    endDate,
  });
}

export const parseApplicationsFilters = (searchParams, _paramKey, fallback) => ({
  selectedStage: searchParams.get('stage')
    ? searchParams.get('stage').split(',').filter(Boolean)
    : fallback.selectedStage,
  selectedAppType: searchParams.get('appType')
    ? searchParams.get('appType').split(',').filter(Boolean)
    : fallback.selectedAppType,
  selectedLeaseDate: {
    startDate: searchParams.get('leaseStart') || fallback.selectedLeaseDate?.startDate || null,
    endDate: searchParams.get('leaseEnd') || fallback.selectedLeaseDate?.endDate || null,
  },
  selectedCreatedDate: parseCreatedDateRangeFromParams(searchParams, fallback.selectedCreatedDate),
  selectedTenantCount: searchParams.get('tenantCount')
    ? searchParams.get('tenantCount').split(',').filter(Boolean)
    : fallback.selectedTenantCount,
});

export const serializeApplicationsFilters = (filters) => ({
  stage:
    Array.isArray(filters.selectedStage) && filters.selectedStage.length > 0
      ? filters.selectedStage.join(',')
      : '',
  appType:
    Array.isArray(filters.selectedAppType) && filters.selectedAppType.length > 0
      ? filters.selectedAppType.join(',')
      : '',
  leaseStart: filters.selectedLeaseDate?.startDate || '',
  leaseEnd: filters.selectedLeaseDate?.endDate || '',
  createdDatePreset: filters.selectedCreatedDate?.preset || '',
  createdDateStart:
    filters.selectedCreatedDate?.preset === 'custom'
      ? filters.selectedCreatedDate?.startDate || ''
      : '',
  createdDateEnd:
    filters.selectedCreatedDate?.preset === 'custom'
      ? filters.selectedCreatedDate?.endDate || ''
      : '',
  tenantCount:
    Array.isArray(filters.selectedTenantCount) && filters.selectedTenantCount.length > 0
      ? filters.selectedTenantCount.join(',')
      : '',
});

export const areArraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

export const areDateRangesEqual = (a = {}, b = {}) =>
  (a?.startDate || null) === (b?.startDate || null) &&
  (a?.endDate || null) === (b?.endDate || null);

export const normalizeArrayFilter = (value) => (Array.isArray(value) ? value : []);

export const normalizeLeaseDateFilter = (value) => ({
  startDate: value?.startDate || null,
  endDate: value?.endDate || null,
});

export {
  areApplicationCreatedDateRangeFiltersEqual,
  normalizeApplicationCreatedDateRangeFilter,
  resolveApplicationCreatedDateRangeForApi,
};

export function buildApplicationListRequestFilter({
  filter,
  selectedStage,
  selectedAppType,
  selectedLeaseDate,
  selectedCreatedDate,
  selectedTenantCount,
}) {
  return {
    ...filter,
    searchText: (filter?.searchText || '').trim(),
    selectedStage,
    selectedAppType,
    selectedLeaseDate,
    selectedCreatedDate: resolveApplicationCreatedDateRangeForApi(selectedCreatedDate),
    selectedTenantCount,
  };
}
